import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import FloatingNav from '../components/FloatingNav';
import BackToHome from '../components/BackToHome';
import SectionTools from '../components/SectionTools';
import { 
  Gift, 
  Award, 
  HeartHandshake, 
  Sparkles, 
  Star, 
  Shield, 
  ArrowRight,
  Users,
  Clock,
  CheckCircle,
  Zap,
  Wallet,
  HandCoins,
  Eye,
  BadgeDollarSign,
  ShieldCheck,
  BarChart3,
  FileSearch
} from 'lucide-react';

// Recovery Bank screenshots
const RECOVERY_IMAGES = {
  controlSheet: 'https://customer-assets.emergentagent.com/job_tap-platform-build/artifacts/3rvsu0en_602BBA94-6A3B-4B16-A31F-B98D244E4D80.png',
  staffMenu: 'https://customer-assets.emergentagent.com/job_tap-platform-build/artifacts/31pz2vtv_IMG_3176.jpeg',
  staffBank: 'https://customer-assets.emergentagent.com/job_tap-platform-build/artifacts/iyqdn2d2_IMG_3177.jpeg',
  issueVoucher: 'https://customer-assets.emergentagent.com/job_tap-platform-build/artifacts/6ea5zmnr_IMG_3179.jpeg',
  voucherIssued: 'https://customer-assets.emergentagent.com/job_tap-platform-build/artifacts/osu2qk1m_IMG_3180.jpeg',
};

const RR360Page = () => {
  const { language } = useLanguage();

  // Navigation sections for floating nav
  const navSections = [
    { id: 'rr360-hero', labelEn: 'R&R 360', labelFr: 'R&R 360' },
    { id: 'moments', labelEn: 'In-Between Moments', labelFr: 'Moments Cles' },
    { id: 'capability', labelEn: 'Capability', labelFr: 'Capacite' },
    { id: 'recovery-bank', labelEn: 'Recovery Bank', labelFr: 'Banque de Recuperation' },
    { id: 'unified-system', labelEn: 'Unified System', labelFr: 'Systeme Unifie' },
    { id: 'permission', labelEn: 'Permission to Lead', labelFr: 'Permission de Diriger' },
    { id: 'retention', labelEn: 'Retention', labelFr: 'Retention' },
  ];

  const content = {
    en: {
      badge: 'R&R 360 — Reward & Resolve',
      title: 'Empowering Teams to Take Care of Guests in Real Time',
      subtitle: 'A staff empowerment capability designed to strengthen guest relationships, support positive reviews, and protect brand reputation.',
      
      momentsTitle: 'Great Service Happens in the In-Between Moments',
      momentsSubtitle: 'In high-standard environments, small moments define the experience. Your teams already want to go above and beyond — R&R 360 gives them a consistent way to do it.',
      
      moment1: 'A regular guest walks in',
      moment2: 'A birthday or special occasion is mentioned',
      moment3: 'A delay happens during a rush',
      moment4: 'A server wants to make it right',
      
      opportunityTitle: 'The Opportunity',
      opportunityText: 'Move from waiting for approval to acting with confidence.',
      
      capabilityTitle: 'Capability, Not Complexity',
      capabilityText: 'R&R 360 is an operational enhancement within the YUL On Tap platform — a clear, structured green light for staff to act in the moment.',
      
      staffCan: 'With R&R 360, staff can:',
      action1: 'Recognize loyal guests',
      action2: 'Reward positive experiences',
      action3: 'Address service moments quickly',
      action4: 'Reinforce the brand standard',
      
      notJust: "It's not just about fixing problems. It's about acknowledging the guests you want back.",

      // Recovery Bank section
      recoveryBadge: 'THE RECOVERY BANK',
      recoveryTitle: 'Reward & Resolve',
      recoverySubtitle: 'Structured Service Recovery in Real Time',

      pillar1Title: 'Accountable Service Recovery',
      pillar1Desc: 'Set a dedicated recovery budget for your team to resolve guest issues instantly. This is a controlled, digital recovery bank that ensures no guest leaves dissatisfied while keeping costs predictable and accountable.',
      pillar1Tag1: 'Defined recovery budget',
      pillar1Tag2: 'Controlled spending limits',
      pillar1Tag3: 'Real-time visibility',
      pillar1Tag4: 'Financial accountability',

      pillar2Title: 'Solve Issues at the Table',
      pillar2Desc: 'Give your staff the authority to resolve service issues immediately. Whether it\'s a long wait or a special VIP moment, recovery vouchers can be issued in seconds without escalation or delay.',
      pillar2Tag1: 'Immediate service recovery',
      pillar2Tag2: 'Staff empowerment',
      pillar2Tag3: 'Faster customer satisfaction',
      pillar2Tag4: 'Professional guest experience',
      pillar2Note: 'The system supports both recovery and recognition — service recovery, guest appreciation, VIP recognition, and loyalty gestures.',

      pillar3Title: 'Full Visibility and Control',
      pillar3Desc: 'Every voucher issued is tracked automatically. Managers can see who issued it, when it was used, and why it was created. This turns service recovery into a measurable operational strategy instead of an uncontrolled expense.',
      pillar3Tag1: 'Complete audit trail',
      pillar3Tag2: 'Remaining balance visibility',
      pillar3Tag3: 'Usage tracking',
      pillar3Tag4: 'Operational transparency',

      recoveryPosition: 'A structured service recovery system — not a discount or coupon tool.',
      
      unifiedTitle: 'A Unified Guest Retention System',
      unifiedSubtitle: 'Three capabilities working together to keep the conversation inside the restaurant — and the guest relationship intact.',
      
      positiveReviews: 'Positive Reviews',
      positiveReviewsDesc: 'Capture great experiences and encourage guests to share their stories publicly.',
      
      reviewShield: 'B-Heard',
      reviewShieldDesc: 'Give guests a private voice to share concerns before they go public.',
      
      rr360: 'R&R 360 — Reward & Resolve',
      rr360Desc: 'Empower staff to recognize and resolve moments in real time.',
      
      syncText: 'When all three work in sync, the guest feels heard, the moment is handled, and the relationship continues.',
      
      permissionTitle: 'Permission to Lead from the Floor',
      
      forStaff: 'For Staff',
      staffBenefit1: 'Confidence to make small decisions',
      staffBenefit2: 'Faster response to guest needs',
      staffBenefit3: 'More ownership of the guest experience',
      
      forManagers: 'For Managers',
      managerBenefit1: 'Consistent use of rewards and recovery',
      managerBenefit2: 'Visibility into guest interactions',
      managerBenefit3: 'Better control without slowing service',
      
      forBrand: 'For the Brand',
      brandBenefit1: 'Consistent hospitality standards',
      brandBenefit2: 'Stronger guest relationships',
      brandBenefit3: 'Fewer escalations, more positive experiences',
      
      retentionTitle: 'Turning Feedback into Retention',
      retentionText: 'With R&R 360, YUL On Tap becomes more than a feedback system — it becomes a guest retention engine.',
      
      stat1Value: '$2-$3',
      stat1Label: 'Starting Gesture',
      stat1Desc: 'Many operators begin with small gestures — the decision always remains with the team and the brand.',
      
      stat2Value: '3',
      stat2Label: 'Integrated Capabilities',
      stat2Desc: 'Positive Reviews, B-Heard, and R&R 360 work together as one retention system.',
      
      stat3Value: '1',
      stat3Label: 'Unified Goal',
      stat3Desc: 'Keep the conversation internal, the guest satisfied, and the loyalty intact.',
      
      tagline: 'Reward great guests. Resolve moments early. Retain loyalty.',
      
      cta: 'Request a Demo',
      explorePlatform: 'Explore the Platform'
    },
    fr: {
      badge: 'R&R 360 — Recompense & Resolution',
      title: 'Permettre aux Equipes de Prendre Soin des Invites en Temps Reel',
      subtitle: 'Une capacite d\'autonomisation du personnel concue pour renforcer les relations avec les invites, soutenir les avis positifs et proteger la reputation de la marque.',
      
      momentsTitle: 'Un Excellent Service Se Produit Dans les Moments Intermediaires',
      momentsSubtitle: 'Dans les environnements de haute qualite, les petits moments definissent l\'experience. Vos equipes veulent deja aller au-dela — R&R 360 leur donne une facon coherente de le faire.',
      
      moment1: 'Un invite regulier arrive',
      moment2: 'Un anniversaire ou une occasion speciale est mentionne',
      moment3: 'Un retard survient pendant une periode d\'affluence',
      moment4: 'Un serveur veut bien faire les choses',
      
      opportunityTitle: 'L\'Opportunite',
      opportunityText: 'Passer de l\'attente d\'approbation a l\'action avec confiance.',
      
      capabilityTitle: 'Capacite, Pas Complexite',
      capabilityText: 'R&R 360 est une amelioration operationnelle au sein de la plateforme YUL On Tap — un feu vert clair et structure pour que le personnel agisse dans l\'instant.',
      
      staffCan: 'Avec R&R 360, le personnel peut:',
      action1: 'Reconnaitre les invites fideles',
      action2: 'Recompenser les experiences positives',
      action3: 'Gerer les moments de service rapidement',
      action4: 'Renforcer la norme de la marque',
      
      notJust: "Il ne s'agit pas seulement de resoudre les problemes. Il s'agit de reconnaitre les invites que vous voulez revoir.",

      // Recovery Bank section
      recoveryBadge: 'LA BANQUE DE RECUPERATION',
      recoveryTitle: 'Recompense & Resolution',
      recoverySubtitle: 'Recuperation de Service Structuree en Temps Reel',

      pillar1Title: 'Recuperation de Service Responsable',
      pillar1Desc: 'Definissez un budget de recuperation dedie pour que votre equipe puisse resoudre instantanement les problemes des invites. Il s\'agit d\'une banque de recuperation numerique et controlee qui garantit qu\'aucun invite ne repart insatisfait tout en maintenant des couts previsibles et verifiables.',
      pillar1Tag1: 'Budget de recuperation defini',
      pillar1Tag2: 'Limites de depenses controlees',
      pillar1Tag3: 'Visibilite en temps reel',
      pillar1Tag4: 'Responsabilite financiere',

      pillar2Title: 'Resoudre les Problemes a la Table',
      pillar2Desc: 'Donnez a votre personnel l\'autorite pour resoudre immediatement les problemes de service. Qu\'il s\'agisse d\'une longue attente ou d\'un moment VIP special, les bons de recuperation peuvent etre emis en quelques secondes sans escalade ni delai.',
      pillar2Tag1: 'Recuperation de service immediate',
      pillar2Tag2: 'Autonomisation du personnel',
      pillar2Tag3: 'Satisfaction client plus rapide',
      pillar2Tag4: 'Experience invites professionnelle',
      pillar2Note: 'Le systeme prend en charge a la fois la recuperation et la reconnaissance — recuperation de service, appreciation des invites, reconnaissance VIP et gestes de fidelite.',

      pillar3Title: 'Visibilite et Controle Complets',
      pillar3Desc: 'Chaque bon emis est automatiquement suivi. Les gestionnaires peuvent voir qui l\'a emis, quand il a ete utilise et pourquoi il a ete cree. Cela transforme la recuperation de service en une strategie operationnelle mesurable plutot qu\'une depense incontrole.',
      pillar3Tag1: 'Piste d\'audit complete',
      pillar3Tag2: 'Visibilite du solde restant',
      pillar3Tag3: 'Suivi d\'utilisation',
      pillar3Tag4: 'Transparence operationnelle',

      recoveryPosition: 'Un systeme structure de recuperation de service — pas un outil de remise ou de coupons.',
      
      unifiedTitle: 'Un Systeme Unifie de Retention des Invites',
      unifiedSubtitle: 'Trois capacites travaillant ensemble pour garder la conversation a l\'interieur du restaurant — et la relation avec l\'invite intacte.',
      
      positiveReviews: 'Avis Positifs',
      positiveReviewsDesc: 'Capturez les excellentes experiences et encouragez les invites a partager leurs histoires publiquement.',
      
      reviewShield: 'B-Heard',
      reviewShieldDesc: 'Offrez aux invités une voix privée pour partager leurs préoccupations avant qu\'elles ne deviennent publiques.',
      
      rr360: 'R&R 360 — Recompense & Resolution',
      rr360Desc: 'Permettez au personnel de reconnaitre et resoudre les moments en temps reel.',
      
      syncText: 'Quand les trois fonctionnent en synchronisation, l\'invite se sent entendu, le moment est gere et la relation continue.',
      
      permissionTitle: 'La Permission de Diriger depuis le Terrain',
      
      forStaff: 'Pour le Personnel',
      staffBenefit1: 'Confiance pour prendre de petites decisions',
      staffBenefit2: 'Reponse plus rapide aux besoins des invites',
      staffBenefit3: 'Plus de responsabilite sur l\'experience invite',
      
      forManagers: 'Pour les Gestionnaires',
      managerBenefit1: 'Utilisation coherente des recompenses et recuperations',
      managerBenefit2: 'Visibilite sur les interactions avec les invites',
      managerBenefit3: 'Meilleur controle sans ralentir le service',
      
      forBrand: 'Pour la Marque',
      brandBenefit1: 'Normes d\'hospitalite coherentes',
      brandBenefit2: 'Relations invites plus fortes',
      brandBenefit3: 'Moins d\'escalades, plus d\'experiences positives',
      
      retentionTitle: 'Transformer les Commentaires en Retention',
      retentionText: 'Avec R&R 360, YUL On Tap devient plus qu\'un systeme de commentaires — il devient un moteur de retention des invites.',
      
      stat1Value: '2$-3$',
      stat1Label: 'Geste de Depart',
      stat1Desc: 'De nombreux operateurs commencent par de petits gestes — la decision reste toujours avec l\'equipe et la marque.',
      
      stat2Value: '3',
      stat2Label: 'Capacites Integrees',
      stat2Desc: 'Avis Positifs, B-Heard et R&R 360 travaillent ensemble comme un seul système de rétention.',
      
      stat3Value: '1',
      stat3Label: 'Objectif Unifie',
      stat3Desc: 'Garder la conversation interne, l\'invite satisfait et la fidelite intacte.',
      
      tagline: 'Recompensez les bons invites. Resolvez les moments tot. Conservez la fidelite.',
      
      cta: 'Demander une Demo',
      explorePlatform: 'Explorer la Plateforme'
    }
  };

  const t = content[language];

  return (
    <div data-testid="rr360-page" className="bg-[#0D0D0D]">
      {/* Floating Navigation */}
      <FloatingNav sections={navSections} />
      <BackToHome />

      {/* Hero Section */}
      <section id="rr360-hero" className="relative min-h-[60vh] flex items-center bg-gradient-to-b from-wine/20 to-[#0D0D0D] scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="flex justify-end mb-4">
            <SectionTools sectionId="rr360-hero" sectionTitle={t.badge} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Gift className="w-8 h-8 text-wine" strokeWidth={1.5} />
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-wine">
                  {t.badge}
                </span>
              </div>
              <h1 
                data-testid="rr360-title"
                className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-tight mb-6"
              >
                {t.title}
              </h1>
              <p className="text-lg sm:text-xl text-white/70 leading-relaxed mb-8">
                {t.subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button 
                    size="lg"
                    data-testid="rr360-cta"
                    className="btn-demo-cta text-white rounded-lg px-8 py-6"
                  >
                    {t.cta}
                  </Button>
                </Link>
                <Link to="/platform">
                  <Button 
                    size="lg"
                    variant="outline"
                    className="bg-transparent text-white border-white/20 hover:bg-white/5 rounded-lg px-8 py-6"
                  >
                    {t.explorePlatform}
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden lg:flex justify-start pl-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_tap-platform-build/artifacts/b9wdzegg_E3440E10-1B89-4B27-A96D-D8660A275127.png"
                alt="Voucher Issued"
                className="w-[420px] h-auto rounded-3xl shadow-2xl shadow-black/50"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* In-Between Moments Section */}
      <section id="moments" className="py-20 lg:py-28 bg-[#0D0D0D] scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-end mb-4">
            <SectionTools sectionId="moments" sectionTitle={t.momentsTitle} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-6 leading-tight">
                {t.momentsTitle}
              </h2>
              <p className="text-lg text-white/60 leading-relaxed mb-8">
                {t.momentsSubtitle}
              </p>
              
              <div className="space-y-4">
                {[t.moment1, t.moment2, t.moment3, t.moment4].map((moment, idx) => (
                  <div key={idx} className="flex items-center gap-4 p-4 bg-white/5 rounded-lg border border-white/10">
                    <div className="w-8 h-8 bg-wine/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-wine font-semibold text-sm">{idx + 1}</span>
                    </div>
                    <span className="text-white/80">{moment}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-wine/20 to-transparent rounded-2xl p-8 border border-wine/20">
              <h3 className="font-heading text-2xl font-semibold text-white mb-4">
                {t.opportunityTitle}
              </h3>
              <p className="text-xl text-white/80 leading-relaxed">
                {t.opportunityText}
              </p>
              <div className="mt-6 flex items-center gap-3">
                <Zap className="w-6 h-6 text-wine" strokeWidth={1.5} />
                <span className="text-wine font-medium">{language === 'fr' ? 'Agir avec Confiance' : 'Act with Confidence'}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Capability Section */}
      <section id="capability" className="py-20 lg:py-28 bg-[#1A1A1A] scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-end mb-4">
            <SectionTools sectionId="capability" sectionTitle={t.capabilityTitle} />
          </div>
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-4">
              {t.capabilityTitle}
            </h2>
            <p className="text-lg text-white/60 max-w-3xl mx-auto">
              {t.capabilityText}
            </p>
          </div>

          <div className="mb-12">
            <h3 className="text-white/70 text-sm font-semibold uppercase tracking-wider mb-6 text-center">
              {t.staffCan}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: Award, text: t.action1 },
                { icon: Sparkles, text: t.action2 },
                { icon: Clock, text: t.action3 },
                { icon: CheckCircle, text: t.action4 }
              ].map((action, idx) => (
                <div key={idx} className="flex items-center gap-3 p-4 bg-white/5 rounded-lg border border-white/10">
                  <action.icon className="w-5 h-5 text-wine flex-shrink-0" strokeWidth={1.5} />
                  <span className="text-white/80">{action.text}</span>
                </div>
              ))}
            </div>
          </div>

          <p className="text-center text-xl text-white/70 italic max-w-2xl mx-auto">
            "{t.notJust}"
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          RECOVERY BANK — Hero-style Feature Showcase with 3 Pillars
          ═══════════════════════════════════════════════════════════════ */}
      <section id="recovery-bank" data-testid="recovery-bank-section" className="py-16 lg:py-24 bg-[#0D0D0D] border-t border-white/5 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-20">
            <span className="inline-block px-5 py-2 bg-emerald-500/10 text-emerald-400 text-xs font-bold uppercase tracking-[0.3em] rounded-full border border-emerald-500/20 mb-6">
              {t.recoveryBadge}
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white mb-4">
              {t.recoveryTitle}
            </h2>
            <p className="text-lg sm:text-xl text-white/50 max-w-2xl mx-auto">
              {t.recoverySubtitle}
            </p>
          </div>

          {/* ─── Pillar 1: The Digital Recovery Bank ─── */}
          <div className="mb-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              {/* Content */}
              <div className="order-2 lg:order-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2.5 bg-emerald-500/10 rounded-xl">
                    <Wallet className="w-6 h-6 text-emerald-400" strokeWidth={1.5} />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400/70">
                    {language === 'fr' ? 'Pilier 1' : 'Pillar 1'}
                  </span>
                </div>
                <h3 className="font-heading text-2xl sm:text-3xl font-semibold text-white mb-4 leading-tight">
                  {t.pillar1Title}
                </h3>
                <p className="text-white/60 leading-relaxed mb-6 text-base sm:text-lg">
                  {t.pillar1Desc}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {[t.pillar1Tag1, t.pillar1Tag2, t.pillar1Tag3, t.pillar1Tag4].map((tag, i) => (
                    <div key={i} className="flex items-center gap-2 p-3 bg-white/5 rounded-lg border border-white/8">
                      <BadgeDollarSign className="w-4 h-4 text-emerald-400 flex-shrink-0" strokeWidth={1.5} />
                      <span className="text-white/70 text-sm">{tag}</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* Screenshot — Desktop browser mockup */}
              <div className="order-1 lg:order-2">
                <div className="bg-[#1A1A1A] rounded-2xl border border-white/10 overflow-hidden shadow-2xl shadow-black/40">
                  <div className="flex items-center gap-2 px-4 py-3 bg-[#111] border-b border-white/5">
                    <div className="flex gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                      <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                      <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
                    </div>
                    <div className="flex-1 mx-8">
                      <div className="bg-white/5 rounded-md px-3 py-1 text-center">
                        <span className="text-[10px] text-white/25">portal.yulontap.com</span>
                      </div>
                    </div>
                  </div>
                  <img src={RECOVERY_IMAGES.controlSheet} alt="Voucher Master Control Sheet" className="w-full" loading="lazy" />
                </div>
              </div>
            </div>
          </div>

          {/* ─── Pillar 2: Empowered Front-Line Resolution ─── */}
          <div className="mb-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              {/* Screenshots — Mobile phone mockup sequence */}
              <div>
                <div className="flex justify-center gap-4 sm:gap-6">
                  {/* Phone 1: Staff Menu */}
                  <div className="w-[160px] sm:w-[200px]">
                    <div className="bg-[#1A1A1A] rounded-[24px] border-2 border-white/10 overflow-hidden shadow-2xl shadow-black/50 p-1.5">
                      <div className="rounded-[18px] overflow-hidden">
                        <img src={RECOVERY_IMAGES.staffMenu} alt="Staff Mobile Menu" className="w-full" loading="lazy" />
                      </div>
                    </div>
                    <p className="text-center text-[10px] text-white/25 mt-2 font-medium uppercase tracking-wider">
                      {language === 'fr' ? 'Menu Personnel' : 'Staff Menu'}
                    </p>
                  </div>
                  {/* Phone 2: Bank Balance */}
                  <div className="w-[160px] sm:w-[200px] mt-8">
                    <div className="bg-[#1A1A1A] rounded-[24px] border-2 border-white/10 overflow-hidden shadow-2xl shadow-black/50 p-1.5">
                      <div className="rounded-[18px] overflow-hidden">
                        <img src={RECOVERY_IMAGES.staffBank} alt="Staff Recovery Bank" className="w-full" loading="lazy" />
                      </div>
                    </div>
                    <p className="text-center text-[10px] text-white/25 mt-2 font-medium uppercase tracking-wider">
                      {language === 'fr' ? 'Ma Banque' : 'My Bank'}
                    </p>
                  </div>
                </div>
              </div>
              {/* Content */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2.5 bg-purple-500/10 rounded-xl">
                    <HandCoins className="w-6 h-6 text-purple-400" strokeWidth={1.5} />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-purple-400/70">
                    {language === 'fr' ? 'Pilier 2' : 'Pillar 2'}
                  </span>
                </div>
                <h3 className="font-heading text-2xl sm:text-3xl font-semibold text-white mb-4 leading-tight">
                  {t.pillar2Title}
                </h3>
                <p className="text-white/60 leading-relaxed mb-6 text-base sm:text-lg">
                  {t.pillar2Desc}
                </p>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {[t.pillar2Tag1, t.pillar2Tag2, t.pillar2Tag3, t.pillar2Tag4].map((tag, i) => (
                    <div key={i} className="flex items-center gap-2 p-3 bg-white/5 rounded-lg border border-white/8">
                      <ShieldCheck className="w-4 h-4 text-purple-400 flex-shrink-0" strokeWidth={1.5} />
                      <span className="text-white/70 text-sm">{tag}</span>
                    </div>
                  ))}
                </div>
                <div className="p-4 bg-purple-500/5 border border-purple-500/15 rounded-xl">
                  <p className="text-white/50 text-sm italic leading-relaxed">
                    {t.pillar2Note}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ─── Pillar 3: Total Management Oversight ─── */}
          <div className="mb-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              {/* Content */}
              <div className="order-2 lg:order-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2.5 bg-amber-500/10 rounded-xl">
                    <Eye className="w-6 h-6 text-amber-400" strokeWidth={1.5} />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400/70">
                    {language === 'fr' ? 'Pilier 3' : 'Pillar 3'}
                  </span>
                </div>
                <h3 className="font-heading text-2xl sm:text-3xl font-semibold text-white mb-4 leading-tight">
                  {t.pillar3Title}
                </h3>
                <p className="text-white/60 leading-relaxed mb-6 text-base sm:text-lg">
                  {t.pillar3Desc}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {[t.pillar3Tag1, t.pillar3Tag2, t.pillar3Tag3, t.pillar3Tag4].map((tag, i) => (
                    <div key={i} className="flex items-center gap-2 p-3 bg-white/5 rounded-lg border border-white/8">
                      <BarChart3 className="w-4 h-4 text-amber-400 flex-shrink-0" strokeWidth={1.5} />
                      <span className="text-white/70 text-sm">{tag}</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* Screenshots — Mobile sequence: Issue + Success */}
              <div className="order-1 lg:order-2">
                <div className="flex justify-center gap-4 sm:gap-6">
                  {/* Phone: Issue Voucher */}
                  <div className="w-[160px] sm:w-[200px]">
                    <div className="bg-[#1A1A1A] rounded-[24px] border-2 border-white/10 overflow-hidden shadow-2xl shadow-black/50 p-1.5">
                      <div className="rounded-[18px] overflow-hidden">
                        <img src={RECOVERY_IMAGES.issueVoucher} alt="Issue Recovery Voucher" className="w-full" loading="lazy" />
                      </div>
                    </div>
                    <p className="text-center text-[10px] text-white/25 mt-2 font-medium uppercase tracking-wider">
                      {language === 'fr' ? 'Emettre un Bon' : 'Issue Voucher'}
                    </p>
                  </div>
                  {/* Phone: Success */}
                  <div className="w-[160px] sm:w-[200px] mt-8">
                    <div className="bg-[#1A1A1A] rounded-[24px] border-2 border-white/10 overflow-hidden shadow-2xl shadow-black/50 p-1.5">
                      <div className="rounded-[18px] overflow-hidden">
                        <img src={RECOVERY_IMAGES.voucherIssued} alt="Voucher Issued Successfully" className="w-full" loading="lazy" />
                      </div>
                    </div>
                    <p className="text-center text-[10px] text-white/25 mt-2 font-medium uppercase tracking-wider">
                      {language === 'fr' ? 'Bon Emis' : 'Voucher Issued'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Recovery Bank Summary Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
            <div className="text-center p-6 bg-emerald-500/5 rounded-xl border border-emerald-500/10">
              <Wallet className="w-6 h-6 text-emerald-400 mx-auto mb-2" strokeWidth={1.5} />
              <p className="text-white font-heading text-lg font-semibold">{language === 'fr' ? 'Budget Controle' : 'Controlled Budget'}</p>
              <p className="text-white/40 text-xs mt-1">{language === 'fr' ? 'Depenses previsibles et limitees' : 'Predictable, capped spending'}</p>
            </div>
            <div className="text-center p-6 bg-purple-500/5 rounded-xl border border-purple-500/10">
              <HandCoins className="w-6 h-6 text-purple-400 mx-auto mb-2" strokeWidth={1.5} />
              <p className="text-white font-heading text-lg font-semibold">{language === 'fr' ? 'Resolution Immediate' : 'Instant Resolution'}</p>
              <p className="text-white/40 text-xs mt-1">{language === 'fr' ? 'Bons emis en quelques secondes' : 'Vouchers issued in seconds'}</p>
            </div>
            <div className="text-center p-6 bg-amber-500/5 rounded-xl border border-amber-500/10">
              <Eye className="w-6 h-6 text-amber-400 mx-auto mb-2" strokeWidth={1.5} />
              <p className="text-white font-heading text-lg font-semibold">{language === 'fr' ? 'Piste d\'Audit Complète' : 'Complete Audit Trail'}</p>
              <p className="text-white/40 text-xs mt-1">{language === 'fr' ? 'Chaque action suivie et enregistree' : 'Every action tracked and logged'}</p>
            </div>
          </div>

          {/* Positioning Statement */}
          <div className="text-center p-6 bg-gradient-to-r from-emerald-500/5 via-purple-500/5 to-amber-500/5 rounded-2xl border border-white/8 max-w-3xl mx-auto">
            <p className="text-white/70 text-lg font-medium italic">
              "{t.recoveryPosition}"
            </p>
          </div>
        </div>
      </section>

      {/* Unified System Section - The Three Capabilities */}
      <section id="unified-system" className="py-20 lg:py-28 bg-[#1A1A1A] border-t border-white/5 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-end mb-4">
            <SectionTools sectionId="unified-system" sectionTitle={t.unifiedTitle} />
          </div>
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-gold/10 text-gold text-xs font-bold uppercase tracking-[0.25em] rounded-full border border-gold/30 mb-6">
              {language === 'fr' ? 'Systeme Unifie' : 'Unified System'}
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-4">
              {t.unifiedTitle}
            </h2>
            <p className="text-lg text-white/60 max-w-3xl mx-auto">
              {t.unifiedSubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {/* Positive Reviews */}
            <Link to="/positive-reviews" className="group">
              <div className="h-full bg-growthgreen/10 border border-growthgreen/30 rounded-2xl p-8 hover:-translate-y-1 hover:border-growthgreen/50 transition-all duration-300">
                <div className="p-3 bg-green-500/10 rounded-xl w-fit mb-6">
                  <Star className="w-6 h-6 text-green-400" fill="currentColor" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl font-medium text-white mb-3">
                  {t.positiveReviews}
                </h3>
                <p className="text-white/50 leading-relaxed mb-4">
                  {t.positiveReviewsDesc}
                </p>
                <div className="flex items-center gap-2 text-green-400 text-sm font-medium">
                  {language === 'fr' ? 'En savoir plus' : 'Learn more'}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
                </div>
              </div>
            </Link>

            {/* B-Heard */}
            <Link to="/b-heard" className="group">
              <div className="h-full bg-darkblue/20 border border-blue-500/30 rounded-2xl p-8 hover:-translate-y-1 hover:border-blue-500/50 transition-all duration-300">
                <div className="p-3 bg-blue-500/10 rounded-xl w-fit mb-6">
                  <Shield className="w-6 h-6 text-blue-400" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl font-medium text-white mb-3">
                  {t.reviewShield}
                </h3>
                <p className="text-white/50 leading-relaxed mb-4">
                  {t.reviewShieldDesc}
                </p>
                <div className="flex items-center gap-2 text-blue-400 text-sm font-medium">
                  {language === 'fr' ? 'En savoir plus' : 'Learn more'}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
                </div>
              </div>
            </Link>

            {/* R&R 360 */}
            <div className="group">
              <div className="h-full bg-wine/20 border border-wine/50 rounded-2xl p-8 relative overflow-hidden">
                <div className="absolute top-3 right-3 px-2 py-1 bg-wine/30 rounded text-xs text-wine font-medium">
                  {language === 'fr' ? 'Page Actuelle' : 'Current Page'}
                </div>
                <div className="p-3 bg-wine/20 rounded-xl w-fit mb-6">
                  <Gift className="w-6 h-6 text-wine" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl font-medium text-white mb-3">
                  {t.rr360}
                </h3>
                <p className="text-white/50 leading-relaxed">
                  {t.rr360Desc}
                </p>
              </div>
            </div>
          </div>

          {/* Sync Message */}
          <div className="text-center p-6 bg-white/5 rounded-xl border border-white/10 max-w-3xl mx-auto">
            <HeartHandshake className="w-8 h-8 text-gold mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-white/80 text-lg">
              {t.syncText}
            </p>
          </div>
        </div>
      </section>

      {/* Permission to Lead Section */}
      <section id="permission" className="py-20 lg:py-28 bg-[#0D0D0D] scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-end mb-4">
            <SectionTools sectionId="permission" sectionTitle={t.permissionTitle} />
          </div>
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-4">
              {t.permissionTitle}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Users, title: t.forStaff, benefits: [t.staffBenefit1, t.staffBenefit2, t.staffBenefit3] },
              { icon: Award, title: t.forManagers, benefits: [t.managerBenefit1, t.managerBenefit2, t.managerBenefit3] },
              { icon: Sparkles, title: t.forBrand, benefits: [t.brandBenefit1, t.brandBenefit2, t.brandBenefit3] },
            ].map((col, idx) => (
              <div key={idx} className="bg-white/5 border border-white/10 rounded-2xl p-8">
                <div className="p-3 bg-wine/10 rounded-xl w-fit mb-6">
                  <col.icon className="w-6 h-6 text-wine" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl font-medium text-white mb-4">{col.title}</h3>
                <ul className="space-y-3">
                  {col.benefits.map((b, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                      <span className="text-white/70">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Retention Stats Section */}
      <section id="retention" className="py-20 lg:py-28 bg-[#1A1A1A] scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-end mb-4">
            <SectionTools sectionId="retention" sectionTitle={t.retentionTitle} />
          </div>
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-4">
              {t.retentionTitle}
            </h2>
            <p className="text-lg text-white/60 max-w-3xl mx-auto">
              {t.retentionText}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {[
              { value: t.stat1Value, label: t.stat1Label, desc: t.stat1Desc },
              { value: t.stat2Value, label: t.stat2Label, desc: t.stat2Desc },
              { value: t.stat3Value, label: t.stat3Label, desc: t.stat3Desc },
            ].map((stat, idx) => (
              <div key={idx} className="text-center p-8 bg-white/5 rounded-2xl border border-white/10">
                <div className="text-4xl sm:text-5xl font-heading font-semibold text-wine mb-2">{stat.value}</div>
                <div className="text-white font-medium mb-2">{stat.label}</div>
                <p className="text-white/50 text-sm">{stat.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <blockquote className="text-2xl sm:text-3xl font-heading text-white/90 italic max-w-3xl mx-auto">
              "{t.tagline}"
            </blockquote>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-b from-[#1A1A1A] to-wine/20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-4">
            {language === 'fr' ? 'Pret a Autonomiser Votre Equipe?' : 'Ready to Empower Your Team?'}
          </h2>
          <p className="text-lg text-white/60 mb-8 max-w-2xl mx-auto">
            {language === 'fr' 
              ? 'Decouvrez comment R&R 360 peut transformer la facon dont votre equipe prend soin des invites.'
              : 'Discover how R&R 360 can transform the way your team takes care of guests.'
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button 
                size="lg"
                className="btn-demo-cta text-white rounded-lg px-8 py-6"
              >
                {t.cta}
              </Button>
            </Link>
            <Link to="/platform">
              <Button 
                size="lg"
                variant="outline"
                className="bg-transparent text-white border-white/20 hover:bg-white/5 rounded-lg px-8 py-6"
              >
                {t.explorePlatform}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RR360Page;
